import React, { useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { ItemForm, ItemsTable } from "domain/Inventory";
import { FiltersGroup } from "domain/Filters";
import { StickersForPrint } from "domain/Print";
import { ScrollPosition, SideBar, useToggler, useModalContext, Button, Spinner } from "shared";
import { GoPlus } from "react-icons/go";
import { HiOutlinePrinter } from "react-icons/hi";
import { ModalConfirmation } from "shared/components/Modal/components/modal-confirmation/modal-confirmation";
import { NavButton } from "shared/components/nav-button/nav-button";
import { SettingsIcon } from "assets";
import { useLocalStorage } from "domain/App/hooks";
import { PreferencesForm } from "domain/Inventory";
import { initialDisplayedColumns, DISPLAYED_COLUMNS } from "shared/constants/constants";
import { DeleteItemForm } from "../../../Inventory/forms/index";

import "./home.page.scss";
import { useInventoryPaginationContext } from "domain/Inventory/context";
import { NoResultsForPageComponent } from "shared/components/NoResultsForPageComponent/NoResultsForPageComponent";

export const HomePage = () => {
  const {
    items,
    totalItems,
    addItem,
    selectedItems,
    isLoading,
    isRefreshingRememberedList,
    updateSelection,
    deleteInventories,
  } = useInventoryPaginationContext();

  const { createModal } = useModalContext();
  const [isCreateBtnLoading, setIsCreateBtnLoading] = useState(false);
  const [createInventoryErrorMessage, setCreateInventoryErrorMessage] = useState("");
  const { isOpen, onOpenHandler, onCloseHandler } = useToggler();
  const navigate = useNavigate();
  const [isBtnLoading, setIsBtnLoading] = useState(false);

  const [displayedColumns, setDisplayedColumns] = useLocalStorage(DISPLAYED_COLUMNS, initialDisplayedColumns);

  const { element: CreateNewItemModal, open, close } = useMemo(() => createModal(), [createModal]);
  const {
    element: CloseNewItemModalConfirmation,
    open: openConfirmationModal,
    close: closeConfirmationModal,
  } = useMemo(() => createModal(), [createModal]);

  const {
    element: DeleteInventoryModal,
    open: openDeleteInventoryModal,
    close: closeDeleteInventoryModal,
  } = useMemo(() => createModal(), [createModal]);

  const onCreateNewItem = useCallback(
    (newItem, count) => {
      setIsCreateBtnLoading(true);
      addItem(newItem, count)
        .then((data) => {
          close();
          data && navigate(`/item/${data[0].itemId}`);
        })
        .catch((error) => {
          console.error("Add item error:", error);
          setCreateInventoryErrorMessage(error?.response?.data || error?.message || "Unknown error");
        })
        .finally(() => setIsCreateBtnLoading(false));
    },
    [addItem, close, navigate]
  );

  const onSubmitConfirmationModal = () => {
    closeConfirmationModal();
    close();
  };

  const onCloseConfirmationModal = () => {
    closeConfirmationModal();
  };

  const onDeleteItems = (archivedReason) => {
    if (!selectedItems || !selectedItems.length) return;
    setIsBtnLoading(true);
    const idsToDelete = selectedItems.map((item) => item.original.id);
    deleteInventories(idsToDelete, archivedReason)
      .then(() => {
        setIsBtnLoading(false);
        alert("Predmeti su uspesno obrisani!");
        closeDeleteInventoryModal();
        navigate("/");
      })
      .catch((error) => {
        setIsBtnLoading(false);
        alert("Predmet nije obrisan. Dogodila se greska pri brisanju predmeta!");
      });
  };

  const {
    element: ChangePreferencesModal,
    open: openPreferencesModal,
    close: closePreferencesModal,
  } = useMemo(() => createModal(), [createModal]);

  const onOpenPreferencesModal = useCallback(() => {
    openPreferencesModal();
  }, [openPreferencesModal]);

  const savePreferences = (values) => {
    setDisplayedColumns(values);
    closePreferencesModal();
  };

  const onClosePreferencesModal = () => {
    closePreferencesModal();
  };

  const onOpenStickerSideBar = () => {
    onOpenHandler();
  };

  const renderContent = () => {
    if (isLoading && items.length === 0) {
      return (
        <div className="items-table__loading">
          <Spinner size="large" />
        </div>
      );
    }

    if (items.length === 0 && totalItems > 0) {
      return <NoResultsForPageComponent />;
    }

    if (items.length === 0) {
      return <div className="items-table__loading">Ne postoji nijedan inventar sa selektovanim filterima.</div>;
    }

    return (
      <div
        className={clsx("home-results", {
          "home-results--loading": isLoading && !isRefreshingRememberedList,
          "home-results--refreshing": isRefreshingRememberedList,
        })}
      >
        {isLoading && !isRefreshingRememberedList && (
          <div className="home-results__overlay">
            <div className="home-results__spinner">
              <Spinner size="large" />
            </div>
          </div>
        )}
        {isRefreshingRememberedList && (
          <div className="home-results__refreshing">
            <Spinner />
          </div>
        )}
        <ScrollPosition uniqueId="dashboard-page">
          <ItemsTable
            displayedColumns={displayedColumns}
            data={items}
            totalItems={totalItems}
            isReadOnly={false}
            updateSelection={updateSelection}
            isUrlDriven
          />
        </ScrollPosition>
      </div>
    );
  };

  return (
    <>
      <div className="home-header">
        <NavButton />
        <div className="home-header_left">
          <FiltersGroup />
        </div>
        <div className="home-header_right">
          <div className="home-header_right_primary_cta">
            <Button
              buttonPrefixIcon={<HiOutlinePrinter className="btn__print" />}
              buttonText={"Štampaj nalepnice"}
              buttonColor={"blue"}
              event={onOpenStickerSideBar}
              isDisabled={!selectedItems.length || isRefreshingRememberedList}
            />

            <Button
              buttonText={"Obriši predmete"}
              buttonColor={"red"}
              event={openDeleteInventoryModal}
              isDisabled={!selectedItems.length || isRefreshingRememberedList}
            />

            <Button
              buttonText={"Novi predmet"}
              buttonIcon={<GoPlus className="btn__plus-icon" />}
              buttonColor={"blue"}
              event={(e) => {
                setIsCreateBtnLoading(false);
                setCreateInventoryErrorMessage("");
                open();
              }}
            />
            <Button buttonIcon={<SettingsIcon className="preference__icon" />} event={onOpenPreferencesModal} />
          </div>
        </div>
      </div>

      {renderContent()}

      <ChangePreferencesModal>
        <PreferencesForm
          displayedColumns={displayedColumns}
          onCancel={onClosePreferencesModal}
          onSubmit={savePreferences}
          title="Preference"
        />
      </ChangePreferencesModal>
      <CreateNewItemModal>
        <ItemForm
          onSubmit={onCreateNewItem}
          onCancel={openConfirmationModal}
          isBtnLoading={isCreateBtnLoading}
          errorMessage={createInventoryErrorMessage}
        />
      </CreateNewItemModal>
      <CloseNewItemModalConfirmation>
        <ModalConfirmation
          onSubmit={onSubmitConfirmationModal}
          onCancel={onCloseConfirmationModal}
          text={<p>Da li ste sigurni da želite da zatvorite prozor?</p>}
        />
      </CloseNewItemModalConfirmation>
      <DeleteInventoryModal>
        <DeleteItemForm
          onSubmit={onDeleteItems}
          onCancel={closeDeleteInventoryModal}
          inventory={selectedItems}
          isBtnLoading={isBtnLoading}
        />
      </DeleteInventoryModal>
      <SideBar onClose={onCloseHandler} isOpen={isOpen} sideBarToggle floatRight>
        <StickersForPrint isOpen={isOpen} onClose={onCloseHandler} selectedItems={selectedItems} />
      </SideBar>
    </>
  );
};
